/**
 * original source: https://ui.shadcn.com/docs/components/sheet
 */
import { XIcon } from 'lucide-react'
import { Dialog as SheetPrimitive } from 'radix-ui'

import { focusRingClassName, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { IconButton } from './Button'
import { isSonnerInteractionTarget } from '../utils/isSonnerInteractionTarget'
import type { ComponentProps } from 'react'

export type SheetProps = ComponentProps<typeof SheetPrimitive.Root>
export const Sheet = SheetPrimitive.Root

export const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

export function SheetCloseIconButton(
  props: Omit<ComponentProps<typeof IconButton>, 'children'>,
) {
  return (
    <SheetClose asChild>
      <IconButton variant="ghost" tooltip="Close" {...props}>
        <XIcon className="size-4" />
      </IconButton>
    </SheetClose>
  )
}

const SheetPortal = SheetPrimitive.Portal

const sheetOverlayVariants = tv({
  base: 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-40',

  defaultVariants: {
    variant: 'light',
  },

  variants: {
    variant: {
      dark: 'bg-black/80',
      dim: 'bg-gray-950/20',
      light: 'bg-tremor-background/80',
    },
  },
})

export type SheetOverlayProps = Omit<
  ComponentProps<typeof SheetPrimitive.Overlay>,
  'className'
> &
  VariantProps<typeof sheetOverlayVariants>

function SheetOverlay({ variant, ...props }: SheetOverlayProps) {
  return (
    <SheetPrimitive.Overlay
      className={sheetOverlayVariants({
        variant,
      })}
      {...props}
    />
  )
}

export const sheetContentVariants = tv({
  base: [
    'fixed z-40 gap-4 bg-tremor-background dark:bg-tremor-background-muted p-6 shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-150',
    'outline-hidden',
    focusRingClassName,
  ],

  defaultVariants: {
    side: 'right',
  },

  variants: {
    layout: {
      column: 'flex flex-col gap-0',
    },
    overflow: {
      hidden: 'overflow-hidden',
    },
    padding: {
      none: 'p-0',
    },
    side: {
      bottom:
        'inset-x-0 bottom-0 border-t border-tremor-border data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
      left: 'inset-y-0 left-0 h-full w-3/4 max-w-full border-r border-tremor-border data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
      right:
        'inset-y-0 right-0 h-full w-3/4 max-w-full border-l border-tremor-border data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
      top: 'inset-x-0 top-0 border-b border-tremor-border data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
    },
    size: {
      lg: 'w-full sm:max-w-xl',
      md: 'w-full sm:max-w-lg',
      panel: 'w-[min(60rem,90vw)] max-w-none sm:max-w-none',
      wide: 'w-full sm:max-w-4xl',
      xl: 'w-full sm:max-w-2xl',
    },
  },
})

export interface SheetContentProps
  extends
    Omit<ComponentProps<typeof SheetPrimitive.Content>, 'className'>,
    VariantProps<typeof sheetContentVariants> {
  overlayProps?: SheetOverlayProps
}

export function SheetContent({
  children,
  layout,
  onInteractOutside,
  onPointerDownOutside,
  overflow,
  overlayProps,
  padding,
  side = 'right',
  size,
  ...props
}: SheetContentProps) {
  const handleInteractOutside: SheetContentProps['onInteractOutside'] = (
    event,
  ) => {
    if (isSonnerInteractionTarget(event.target)) {
      event.preventDefault()
      return
    }

    onInteractOutside?.(event)
  }

  const handlePointerDownOutside: SheetContentProps['onPointerDownOutside'] = (
    event,
  ) => {
    if (isSonnerInteractionTarget(event.target)) {
      event.preventDefault()
      return
    }

    onPointerDownOutside?.(event)
  }

  return (
    <SheetPortal>
      <SheetOverlay {...overlayProps} />
      <SheetPrimitive.Content
        className={sheetContentVariants({
          layout,
          overflow,
          padding,
          side,
          size,
        })}
        {...props}
        onInteractOutside={handleInteractOutside}
        onPointerDownOutside={handlePointerDownOutside}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

const sheetHeaderVariants = tv({
  base: 'flex text-center sm:text-left',

  defaultVariants: {
    layout: 'stack',
  },

  variants: {
    layout: {
      stack: 'flex-col gap-y-2',
      toolbar: 'flex-row items-center justify-between gap-x-3',
    },
    padding: {
      md: 'px-6 py-4',
      sm: 'px-4 py-3',
    },
  },
})

export type SheetHeaderProps = Omit<ComponentProps<'div'>, 'className'> &
  VariantProps<typeof sheetHeaderVariants>

export function SheetHeader({ layout, padding, ...props }: SheetHeaderProps) {
  return (
    <div
      className={sheetHeaderVariants({
        layout,
        padding,
      })}
      {...props}
    />
  )
}

const sheetTitleVariants = tv({
  base: 'text-tremor-content-strong font-semibold',

  defaultVariants: {
    size: 'lg',
  },

  variants: {
    size: {
      lg: 'text-lg',
      sm: 'text-sm leading-5',
    },
    wrap: {
      break: 'wrap-break-word',
    },
  },
})

export type SheetTitleProps = Omit<
  ComponentProps<typeof SheetPrimitive.Title>,
  'className'
> &
  VariantProps<typeof sheetTitleVariants>

export function SheetTitle({ size, wrap, ...props }: SheetTitleProps) {
  return (
    <SheetPrimitive.Title
      className={sheetTitleVariants({ size, wrap })}
      {...props}
    />
  )
}

export type SheetDescriptionProps = Omit<
  ComponentProps<typeof SheetPrimitive.Description>,
  'className'
>

export function SheetDescription(props: SheetDescriptionProps) {
  return (
    <SheetPrimitive.Description
      className="text-muted-foreground text-sm"
      {...props}
    />
  )
}
